import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Input, Space, Tag, Avatar, Button, Badge, Select } from 'antd';
import { SearchOutlined, UserOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { UserFilters, UserFilterValues } from '../../components/filters/UserFilters';

interface Passenger {
  id: number;
  fullName: string | null;
  phone: string;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  photoUrl: string | null;
  totalTrips: number;
  createdAt: string;
}

export const PassengerList = () => {
  const { show } = useNavigation();
  const [searchText, setSearchText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<UserFilterValues>({});

  const activeFilterCount = Object.values(activeFilters).filter(
    (v) => v !== undefined && v !== '' && v !== null
  ).length;

  const { tableProps, setFilters } = useTable<Passenger>({
    resource: 'admin/users',
    syncWithLocation: true,
    filters: {
      permanent: [{ field: 'role', operator: 'eq', value: 'CLIENT' }],
    },
  });

  const handleApplyFilters = (filters: UserFilterValues) => {
    setActiveFilters(filters);

    const refineFilters: any[] = [];

    if (filters.search) {
      refineFilters.push({ field: 'search', operator: 'contains', value: filters.search });
    }
    if (filters.phone) {
      refineFilters.push({ field: 'phone', operator: 'contains', value: filters.phone });
    }
    if (filters.status) {
      refineFilters.push({ field: 'status', operator: 'eq', value: filters.status });
    }
    if (filters.minTrips !== undefined) {
      refineFilters.push({ field: 'minTrips', operator: 'gte', value: filters.minTrips });
    }
    if (filters.maxTrips !== undefined) {
      refineFilters.push({ field: 'maxTrips', operator: 'lte', value: filters.maxTrips });
    }
    if (filters.registeredFrom) {
      refineFilters.push({ field: 'registeredFrom', operator: 'gte', value: filters.registeredFrom });
    }
    if (filters.registeredTo) {
      refineFilters.push({ field: 'registeredTo', operator: 'lte', value: filters.registeredTo });
    }

    setFilters(refineFilters);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    setFilters([]);
  };

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'default',
    BLOCKED: 'red',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Faol',
    OFFLINE: 'Nofaol',
    BLOCKED: 'Bloklangan',
  };

  const columns = [
    {
      title: "Yo'lovchi",
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: string, record: Passenger) => (
        <Space>
          <Avatar src={record.photoUrl} icon={<UserOutlined />} />
          <div>
            <div>{record.fullName || "Ismi yo'q"}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Sayohatlar',
      dataIndex: 'totalTrips',
      key: 'totalTrips',
    },
    {
      title: "Ro'yxatdan o'tgan",
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: Passenger) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/passengers', record.id)}
        />
      ),
    },
  ];

  return (
    <>
      <List
        title="Yo'lovchilar"
        headerButtons={
          <Space>
            <Input
              placeholder="Qidirish..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
              onPressEnter={() => handleApplyFilters({ ...activeFilters, search: searchText })}
            />
            <Select
              placeholder="Holat"
              allowClear
              style={{ width: 130 }}
              value={activeFilters.status}
              onChange={(value) => handleApplyFilters({ ...activeFilters, status: value })}
              options={[
                { value: 'ONLINE', label: 'Faol' },
                { value: 'OFFLINE', label: 'Nofaol' },
                { value: 'BLOCKED', label: 'Bloklangan' },
              ]}
            />
            <Badge count={activeFilterCount} size="small">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen(true)}
              >
                Filtr
              </Button>
            </Badge>
          </Space>
        }
      >
        <Table {...tableProps} columns={columns} rowKey="id" />
      </List>

      <UserFilters
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        filterType="passengers"
        initialValues={activeFilters}
      />
    </>
  );
};
